// src/store/slices/heliaSlice.ts
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { HeliaStatus, FileMetadata, UploadProgress, UploadStage } from '@/types/poc/helia';
import {
  addFileToHeliaThunk as addFileToHelia,
  downloadAndDecryptFileThunk as downloadAndDecryptFile,
  downloadFileFromNetwork,
  saveFileToFilesystem,
} from '@/store/thunks/poc/helia';

const PROGRESS_COMPLETE = 100;

// Define the Helia state interface
export interface HeliaState {
  status: HeliaStatus;
  files: Record<string, FileMetadata>; // CID string -> FileMetadata
  errorMessage: string | null;
  currentOperation: string | null;

  // Upload progress tracking (by upload ID)
  uploadProgress: Record<string, UploadProgress>;

  // Network file operations state
  networkOperations: Record<string, {
      status: 'requesting' | 'downloading' | 'completed' | 'failed';
      progress: number;
      error?: string;
    }>;
}

// Initial state
const initialState: HeliaState = {
  status: 'idle',
  files: {},
  errorMessage: null,
  currentOperation: null,
  uploadProgress: {},
  networkOperations: {},
};

const heliaSlice = createSlice({
  name: 'helia',
  initialState,
  reducers: {
    // Pure function for manually setting the node (useful for testing)
    // Add file metadata manually
    addFileMetadata: (state, action: PayloadAction<FileMetadata>) => {
      const { payload } = action;
      const { cid } = payload;
      state.files[cid] = payload;
    },

    // Remove file metadata
    removeFileMetadata: (state, action: PayloadAction<string>) => {
      const { payload } = action;
      const { [payload]: removedFile, ...remainingFiles } = state.files;
      state.files = remainingFiles;
      // Also clean up network operation state
      const { [payload]: removedOp, ...remainingOps } = state.networkOperations;
      state.networkOperations = remainingOps;
    },

    // Network operation progress tracking
    setNetworkOperationStatus: (
      state,
      action: PayloadAction<{
        cid: string;
        status: HeliaState['networkOperations'][string]['status'];
        progress?: number;
        error?: string;
      }>
    ) => {
      const { payload } = action;
      const { cid, status, progress = 0, error } = payload;
      state.networkOperations[cid] = {
        status,
        progress,
        ...((error != null) && { error })
      };
    },

    // Clear network operation tracking
    clearNetworkOperation: (state, action: PayloadAction<string>) => {
      const { payload } = action;
      const { [payload]: removedOp, ...remainingOps } = state.networkOperations;
      state.networkOperations = remainingOps;
    },

    // Upload progress tracking
    setUploadProgress: (
      state,
      action: PayloadAction<{
        uploadId: string;
        fileName: string;
        path?: string;
        cid?: string;
        stage: UploadStage;
        progress: number;
        error?: string;
        hapBytesTransferred?: number;
        hapTotalBytes?: number;
        sixId?: string;
        metadata?: FileMetadata;
        retryCount?: number;
      }>
    ) => {
      const { payload } = action;
      const existing = state.uploadProgress[payload.uploadId];
      state.uploadProgress[payload.uploadId] = {
        ...existing,
        ...payload,
        error: payload.stage === 'error' ? payload.error : undefined,
      };
    },

    // Clear upload progress for a file
    clearUploadProgress: (state, action: PayloadAction<string>) => {
      const { payload: uploadId } = action;
      const { [uploadId]: removedProgress, ...remainingProgress } = state.uploadProgress;
      state.uploadProgress = remainingProgress;
    },

    // Clear error message
    clearError: (state) => {
      state.errorMessage = null;
    },
  },

  extraReducers: (builder) => {
    builder
      // Add file cases
      .addCase(addFileToHelia.pending, (state) => {
        state.currentOperation = 'Adding file to IPFS';
      })
      .addCase(addFileToHelia.fulfilled, (state, action) => {
        const { payload } = action;
        state.files[payload.metadata.cid] = payload.metadata;
        state.currentOperation = null;
      })
      .addCase(addFileToHelia.rejected, (state, action) => {
        state.errorMessage = typeof action.payload === 'string' ? action.payload : 'Failed to add file';
        state.currentOperation = null;
      })

      // ===== NEW NETWORK FILE CASES =====

      // Download from network cases
      .addCase(downloadFileFromNetwork.pending, (state, action) => {
        const { meta: { arg: { cid } } } = action;
        state.currentOperation = `Downloading ${cid} from network`;
        state.networkOperations[cid] = {
          status: 'requesting',
          progress: 0
        };
      })
      .addCase(downloadFileFromNetwork.fulfilled, (state, action) => {
        const { meta: { arg: { cid } } } = action;
        state.currentOperation = null;
        state.networkOperations[cid] = {
          status: 'completed',
          progress: PROGRESS_COMPLETE
        };
      })
      .addCase(downloadFileFromNetwork.rejected, (state, action) => {
        const { meta: { arg: { cid } }, error: { message } } = action;
        state.currentOperation = null;
        state.errorMessage = message ?? 'Failed to download file';
        state.networkOperations[cid] = {
          status: 'failed',
          progress: 0,
          error: action.error.message
        };
      })

      // Save to filesystem cases
      .addCase(saveFileToFilesystem.pending, (state) => {
        state.currentOperation = 'Saving file to filesystem';
      })
      .addCase(saveFileToFilesystem.fulfilled, (state) => {
        state.currentOperation = null;
      })
      .addCase(saveFileToFilesystem.rejected, (state, action) => {
        const { error: { message } } = action;
        state.currentOperation = null;
        state.errorMessage = message ?? 'Failed to save file';
      })

      // Download and decrypt cases
      .addCase(downloadAndDecryptFile.pending, (state) => {
        state.currentOperation = 'Downloading and decrypting file';
      })
      .addCase(downloadAndDecryptFile.fulfilled, (state) => {
        state.currentOperation = null;
      })
      .addCase(downloadAndDecryptFile.rejected, (state, action) => {
        const { error: { message } } = action;
        state.currentOperation = null;
        state.errorMessage = message ?? 'Failed to download and decrypt file';
      })

      ;
  },
});

const { actions } = heliaSlice;
export const {
  addFileMetadata,
  removeFileMetadata,
  setNetworkOperationStatus,
  clearNetworkOperation,
  setUploadProgress,
  clearUploadProgress,
  clearError,
} = actions;

export default heliaSlice.reducer;
